// src/components/PreviewUserProfile.js - Updated with ghost link functionality, 3:1 banner aspect ratio, and blurred background container
import React, { useState, useEffect } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

// Normal user profile CSS (for the final preview if unlocked)
import styles from "./UserProfile.module.css";

// The lock screen UI with the floating ghost icon
import GhostProfile from "./GhostProfile"; 

// Import GhostLinkButton for ghost-protected links
import GhostLinkButton from "./GhostLinkButton";

// For hashing if needed, or you might have a function from GhostMode
import { hashPassword } from "./GhostMode";

// Utility stuff, icons, etc.
import availableIcons from "./icons";
import { Mail, Phone} from "lucide-react";
import { getPaletteColors } from "../utils/templateUtils";

const PreviewUserProfile = ({ userData }) => {
  const [currentTheme, setCurrentTheme] = useState(userData?.profile?.theme || "classic");
  const [isGhostAuthenticated, setIsGhostAuthenticated] = useState(false);
  
  // Add states for ghost link functionality
  const [unlockedLinks, setUnlockedLinks] = useState({});
  const [ghostLinkInputs, setGhostLinkInputs] = useState({});
  const [ghostLinkErrors, setGhostLinkErrors] = useState({});

  useEffect(() => {
    if (userData?.profile?.theme) {
      setCurrentTheme(userData.profile.theme);
    }
  }, [userData?.profile?.theme]);

  // New effect to ensure banner has 3:1 aspect ratio
  useEffect(() => {
    // Function to ensure banner has 3:1 aspect ratio
    const setBannerAspectRatio = () => {
      const bannerContainers = document.querySelectorAll('.banner-container');
      bannerContainers.forEach(container => {
        // Calculate height based on width for 3:1 aspect ratio
        const containerWidth = container.offsetWidth;
        const bannerHeight = containerWidth / 3; 
        
        // Apply the calculated height
        container.style.height = `${bannerHeight}px`;
        container.style.maxHeight = `${bannerHeight}px`;
        container.style.minHeight = `${bannerHeight}px`;
        container.style.boxSizing = 'border-box';
        container.style.overflow = 'hidden';
      });
    };
    
    // Run when component mounts
    if (userData) {
      setTimeout(setBannerAspectRatio, 100);
    }
    
    // Run on window resize
    window.addEventListener('resize', setBannerAspectRatio);
    
    return () => {
      window.removeEventListener('resize', setBannerAspectRatio);
    };
  }, [userData]);

  // Mobile scrolling fix - added to ensure all links are visible on mobile
  useEffect(() => {
    // Fix for mobile preview scrolling issues
    const fixPreviewScrolling = () => {
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile) {
        // Set custom viewport height variable
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        
        // Add padding to containers in the preview
        const profileContainer = document.querySelector('.profileContainer') || 
                                document.querySelector('.profile-container');
        
        if (profileContainer) {
          profileContainer.style.minHeight = `calc(100 * var(--vh) + 200px)`;
          profileContainer.style.paddingBottom = '180px';
        }
        
        // Extra padding for center container
        const centerContainer = document.querySelector('.centerContainer') ||
                              document.querySelector('.center-container');
        if (centerContainer) {
          centerContainer.style.paddingBottom = '120px';
        }
        
        // Add padding to links container
        const linksContainer = document.querySelector('.links-container');
        if (linksContainer) {
          linksContainer.style.paddingBottom = '120px';
        }
      }
    };
    
    // Run on mount
    fixPreviewScrolling();
    
    // Run on resize and orientation change
    window.addEventListener('resize', fixPreviewScrolling);
    window.addEventListener('orientationchange', () => {
      setTimeout(fixPreviewScrolling, 100);
    });
    
    return () => {
      window.removeEventListener('resize', fixPreviewScrolling);
      window.removeEventListener('orientationchange', fixPreviewScrolling);
    };
  }, []);

  // Set link background color and opacity for CSS variables
  useEffect(() => {
    if (userData?.profile?.palette?.colors && userData.profile.palette.colors.length > 0) {
      document.documentElement.style.setProperty('--link-background', userData.profile.palette.colors[0]);
    }
    
    // Update the link opacity variable if it exists in the user data
    if (userData?.profile?.palette?.linkOpacity !== undefined) {
      document.documentElement.style.setProperty('--link-background-opacity', userData.profile.palette.linkOpacity / 100);
    }
  }, [userData]);

  // The ghost mode object from Firestore
  const ghostMode = userData?.profile?.ghostMode;

  // We'll define the function we pass to GhostProfile's onPasswordSubmit prop:
  const handleGhostPasswordSubmit = async (plainPw) => {
    // Return { success: true } or { success: false } 
    // after checking Firestore. We'll do the IP-based logic:

    if (!ghostMode || !ghostMode.enabled) {
      return { success: false }; 
    }

    try {
      const hashedInput = await hashPassword(plainPw.trim());

      // Get user's IP & see how many times they've tried
      const ipRes = await fetch("https://api.ipify.org?format=json");
      const { ip } = await ipRes.json();

      const failedAttempts = ghostMode.failedAttempts || {};
      const lastAttempt = failedAttempts[ip];
      const lockoutDuration = 30 * 60 * 1000; // 30 min

      // If they've tried 4+ times recently, lock them out
      if (lastAttempt) {
        const timeSince = Date.now() - lastAttempt.timestamp;
        if (lastAttempt.count >= 4 && timeSince < lockoutDuration) {
          return { success: false }; // we won't say why, but it's locked
        }
        if (timeSince >= lockoutDuration) {
          delete failedAttempts[ip]; // reset if 30 min passed
        }
      }

      // Compare hashed input
      const storedHash = ghostMode.password?.hash;
      if (storedHash && hashedInput === storedHash) {
        // success => clear attempts, set isGhostAuthenticated
        if (failedAttempts[ip]) {
          delete failedAttempts[ip];
          await updateDoc(doc(db, "users", userData.uid), {
            "profile.ghostMode.failedAttempts": failedAttempts,
          });
        }
        setIsGhostAuthenticated(true);
        return { success: true };
      } else {
        // fail => increment attempt count
        const newAttempts = {
          ...failedAttempts,
          [ip]: {
            count: (failedAttempts[ip]?.count || 0) + 1,
            timestamp: Date.now(),
          },
        };
        await updateDoc(doc(db, "users", userData.uid), {
          "profile.ghostMode.failedAttempts": newAttempts,
        });
        return { success: false };
      }
    } catch (err) {
      console.error("Ghost check error in preview:", err);
      return { success: false };
    }
  };

  // Add handlers for individual ghost link functionality
  const handleGhostLinkSubmit = async (linkId) => {
    const link = userData.profile.links.find((l) => l.id === linkId);
    const input = ghostLinkInputs[linkId] || "";
    
    if (!link || !link.ghostPassword) {
      setGhostLinkErrors((prev) => ({
        ...prev,
        [linkId]: "Invalid link or password",
      }));
      return;
    }
    
    if (input === link.ghostPassword) {
      setUnlockedLinks((prev) => ({ ...prev, [linkId]: true }));
      setGhostLinkErrors((prev) => ({ ...prev, [linkId]: "" }));
    } else {
      setGhostLinkErrors((prev) => ({
        ...prev,
        [linkId]: "Incorrect password",
      }));
    }
  };

  // Handle password input change for ghost links
  const handlePasswordChange = (linkId, value) => {
    setGhostLinkInputs((prev) => ({
      ...prev,
      [linkId]: value,
    }));
    // Clear error when typing
    if (ghostLinkErrors[linkId]) {
      setGhostLinkErrors((prev) => ({
        ...prev,
        [linkId]: "",
      }));
    }
  };

  // If ghost mode is on, but we haven't authenticated => show the Ghost lock screen
  if (ghostMode?.enabled && !isGhostAuthenticated) {
    return (
      <GhostProfile
        username={userData.username}
        customMessage={ghostMode.customMessage}
        onPasswordSubmit={handleGhostPasswordSubmit}
      />
    );
  }

  // Otherwise, show the normal profile preview
  if (!userData) return <div>No preview data available</div>;

  const profile = userData.profile || {};
  const paletteColors = getPaletteColors(userData);

  const backgroundColor = paletteColors[4] || "#ffffff";
  const containerBackground = paletteColors[3] || "#f5f5f5";
  const linkBackground = paletteColors[1] || "#f13535";

  // Some pro logic
  const isProUser = userData.tierType === "pro";
  const getDarkestColor = () => {
    const colors = profile.palette?.colors;
    if (!colors?.length) return "#000000";
    return colors.reduce((darkest, current) => {
      const hex = current.replace("#", "");
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;
      const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;

      const oldHex = darkest.replace("#", "");
      const dr = parseInt(oldHex.slice(0, 2), 16) / 255;
      const dg = parseInt(oldHex.slice(2, 4), 16) / 255;
      const db = parseInt(oldHex.slice(4, 6), 16) / 255;
      const oldLum = 0.2126 * dr + 0.7152 * dg + 0.0722 * db;

      return lum < oldLum ? current : darkest;
    }, colors[0]);
  };
  const defaultIconColor = isProUser ? getDarkestColor() : "#000000";

  const bannerData = profile.banner;
  const hasBannerImage = !!bannerData?.url;

  // Filter & sort links
  const unsortedLinks = profile.links?.filter((link) => !link.hidden) || [];
  const visibleLinks = [...unsortedLinks].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return a.order - b.order;
  });

  const getTextColorWithOpacity = () => {
    const defaultColor = "#444444";
    const defaultOpacity = 70;
    const textColor = profile.textColor || defaultColor;
    if (textColor.length === 9) return textColor;
    const alphaHex = Math.round((profile.textOpacity || defaultOpacity) / 100 * 255)
      .toString(16)
      .padStart(2, "0");
    return `${textColor}${alphaHex}`;
  };
  const textColorWithOpacity = getTextColorWithOpacity();

  const getIconComponent = (iconName) => {
    const iconObj = availableIcons.find((ic) => ic.name === iconName);
    return iconObj ? iconObj.component : null;
  };

  return (
    <div
      className={`${styles.profileContainer} theme-${currentTheme} profile-container`}
      style={{ backgroundColor }}
    >
      <div
        className={`${styles.centerContainer} center-container ${
          hasBannerImage ? "has-background-image" : ""
        }`}
        style={{ backgroundColor: containerBackground }}
      >
        {/* Blurred background container */}
        <div className="blurred-content-container"></div>
        
        {bannerData?.url && (
          <div className="banner-container" style={{ aspectRatio: '3/1' }}>
            <img
              src={bannerData.url}
              alt="Banner"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: `center ${bannerData.position}%`,
              }}
            />
          </div>
        )}

        <div className="profile-content">
          <div className="avatar-container">
            {userData.photoURL ? (
              <img
                src={userData.photoURL}
                alt="Profile"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <div className="initials">
                {(profile.displayName || userData.username || "U")[0].toUpperCase()}
              </div>
            )}
          </div>

          <h1 style={{ color: textColorWithOpacity }}>
            {profile.displayName || userData.username}
          </h1>

          {(profile.email || profile.phone) && (
            <div className={styles.contactInfo}>
              {profile.email && (
                <p
                  className={`${styles.contactItem} email`}
                  style={{ color: textColorWithOpacity }}
                >
                  <Mail size={16} style={{ marginRight: "8px" }} />
                  <span>{profile.email}</span>
                </p>
              )}
              {profile.phone && (
                <p
                  className={`${styles.contactItem} phone`}
                  style={{ color: textColorWithOpacity }}
                >
                  <Phone size={16} style={{ marginRight: "8px" }} />
                  <span>{profile.phone}</span>
                </p>
              )}
            </div>
          )}

          {profile.bio && (
            <p className="bio" style={{ color: textColorWithOpacity }}>
              {profile.bio}
            </p>
          )}

          {profile.icons?.length > 0 && (
            <div className="social-icons">
              {profile.icons.map((iconData, i) => {
                const IconComp = getIconComponent(iconData.component);
                if (!IconComp) return null;
                return (
                  <a
                    key={i}
                    className="icon"
                    href={iconData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: iconData.color || defaultIconColor }}
                  >
                    <IconComp
                      size={24}
                      stroke={iconData.color || defaultIconColor}
                      color={iconData.color || defaultIconColor}
                    />
                  </a>
                );
              })}
            </div>
          )}

          {visibleLinks.length > 0 && (
            <div className="links-container">
              {visibleLinks.map((link, i) => {
                if (link.ghostProtected) {
                  // Use GhostLinkButton for ghost-protected links
                  return (
                    <GhostLinkButton
                      key={i}
                      link={link}
                      isUnlocked={unlockedLinks[link.id]}
                      themeClass={`theme-${currentTheme}`}
                      paletteColors={paletteColors}
                      passwordInput={ghostLinkInputs[link.id]}
                      onPasswordChange={handlePasswordChange}
                      onPasswordSubmit={handleGhostLinkSubmit}
                      error={ghostLinkErrors[link.id]}
                      textColor={textColorWithOpacity}
                      linkBackground={linkBackground}
                      borderColor="var(--link-border, 2px solid #000000)"
                      // Do not set opacity directly as inline style
                    />
                  );
                }
                
                // Regular links
                return (
                  <a
                    key={i}
                    className={`link-button ${link.imageUrl ? "has-image" : ""}`}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: textColorWithOpacity,
                      // Do not set background or opacity directly here
                    }}
                  >
                    {link.imageUrl && (
                      <div className="link-image-container">
                        <img src={link.imageUrl} alt="" className="link-thumbnail" />
                      </div>
                    )}
                    <span className="link-title">{link.title}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewUserProfile;